import { useCallback, useEffect } from 'react';
import { apiFetch } from '../../../lib/api.js';
import { getRunController, setRunMessages, setRunning } from '../../../lib/agentRunStore.js';
import { ABORT_REASON, abortWithReason } from '../../../lib/loop/abortReason.js';

/**
 * Stop-agent logic + ESC keybinding. Aborts the run controller,
 * cancels any spec generation, fires turn-abort to the server,
 * and sweeps in-flight tool cards to aborted.
 *
 * @param {{ sessionId: string|undefined, streaming: boolean, localRunning: boolean, generatingSpec: boolean, specGenAbortRef: React.RefObject, abort: Function, setGeneratingSpec: Function, setLiveStatus: Function }} opts
 */
export function useStopAgent({
  sessionId, streaming, localRunning, generatingSpec,
  specGenAbortRef, abort, setGeneratingSpec, setLiveStatus,
}) {
  const stopAgent = useCallback(() => {
    abortWithReason(getRunController(sessionId), ABORT_REASON.USER_STOP);
    try { specGenAbortRef.current?.abort(); } catch { /* noop */ }
    abort();
    setRunning(sessionId, false);
    setGeneratingSpec(false);
    setLiveStatus(null);
    if (sessionId) {
      apiFetch(`/api/code-ide/sessions/${sessionId}/turn-abort`, { method: 'POST' }).catch(() => {});
    }
    setRunMessages(sessionId, (m) => {
      const swept = m.map((x) =>
        x.role === 'tool' && x.status === 'running'
          ? { ...x, status: 'aborted', result: 'Interrompido.' }
          : x.role === 'agent' && x.streaming
            ? { ...x, streaming: false }
            : x,
      );
      return [
        ...swept,
        {
          id: `local-stop-${Date.now()}`,
          role: 'system',
          content: '⏹️ Interrompido pelo usuário.',
          created_at: new Date().toISOString(),
        },
      ];
    });
  }, [abort, sessionId]);

  useEffect(() => {
    if (!(localRunning || streaming || generatingSpec)) return undefined;
    function onEsc(e) {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      stopAgent();
    }
    window.addEventListener('keydown', onEsc, true);
    return () => window.removeEventListener('keydown', onEsc, true);
  }, [localRunning, streaming, generatingSpec, stopAgent]);

  return stopAgent;
}
